import { useEntity, humanizeEntityRef } from '@backstage/plugin-catalog-react';
import Box from '@material-ui/core/Box';
import Modal from '@material-ui/core/Modal';
import { makeStyles } from '@material-ui/core/styles';
import FolderIcon from '@material-ui/icons/Folder';
import FileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import Alert from '@material-ui/lab/Alert';
import React, { useState, useEffect, Fragment } from 'react';
import useAsync from 'react-use/esm/useAsync';
import { codeCoverageApiRef } from '../../api.esm.js';
import { FileContent } from './FileContent.esm.js';
import { Progress, ResponseErrorPanel, Table } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2)
  },
  icon: {
    marginRight: theme.spacing(1)
  },
  link: {
    color: theme.palette.primary.main,
    cursor: "pointer"
  }
}));
const groupByPath = (files) => {
  const acc = {};
  files.forEach((file) => {
    const filename = file.filename;
    if (!file.filename) return;
    const pathArray = filename?.split("/").filter((el) => el !== "");
    if (pathArray) {
      if (!acc.hasOwnProperty(pathArray[0])) {
        acc[pathArray[0]] = [];
      }
      acc[pathArray[0]].push(file);
    }
  });
  return acc;
};
const removeVisitedPathGroup = (files, pathGroup) => {
  return files.map((file) => {
    return {
      ...file,
      filename: file.filename ? file.filename.substring(
        file.filename?.indexOf(pathGroup) + pathGroup.length + 1
      ) : file.filename
    };
  });
};
const buildFileStructure = (row) => {
  const dataGroupedByPath = groupByPath(row.files);
  row.files = Object.keys(dataGroupedByPath).map((pathGroup) => {
    return buildFileStructure({
      path: pathGroup,
      files: dataGroupedByPath.hasOwnProperty("files") ? removeVisitedPathGroup(dataGroupedByPath.files, pathGroup) : removeVisitedPathGroup(dataGroupedByPath[pathGroup], pathGroup),
      coverage: dataGroupedByPath[pathGroup].reduce(
        (acc, cur) => acc + cur.coverage,
        0
      ) / dataGroupedByPath[pathGroup].length,
      missing: dataGroupedByPath[pathGroup].reduce(
        (acc, cur) => acc + cur.missing,
        0
      ),
      tracked: dataGroupedByPath[pathGroup].reduce(
        (acc, cur) => acc + cur.tracked,
        0
      )
    });
  });
  return row;
};
const formatInitialData = (value) => {
  return buildFileStructure({
    path: "",
    coverage: value.aggregate.line.percentage,
    missing: value.aggregate.line.missed,
    tracked: value.aggregate.line.available,
    files: value.files.map((fc) => {
      return {
        path: "",
        filename: fc.filename,
        coverage: Object.values(fc.lineHits).length > 0 ? Math.floor(
          Object.values(fc.lineHits).filter((hits) => hits > 0).length / Object.values(fc.lineHits).length * 100
        ) : 0,
        missing: Object.values(fc.lineHits).filter((hits) => !hits).length,
        tracked: Object.values(fc.lineHits).length
      };
    })
  });
};
const getObjectsAtPath = (curData, path) => {
  let data = curData?.files;
  for (const fragment of path) {
    data = data?.find((d) => d.path === fragment)?.files;
  }
  return data;
};
const FileExplorer = () => {
  const styles = useStyles();
  const { entity } = useEntity();
  const [curData, setCurData] = useState();
  const [tableData, setTableData] = useState();
  const [curPath, setCurPath] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [curFile, setCurFile] = useState("");
  const codeCoverageApi = useApi(codeCoverageApiRef);
  const { loading, error, value } = useAsync(
    async () => await codeCoverageApi.getCoverageForEntity({
      kind: entity.kind,
      namespace: entity.metadata.namespace || "default",
      name: entity.metadata.name
    })
  );
  useEffect(() => {
    if (!value) return;
    const data = formatInitialData(value);
    setCurData(data);
    if (data.files) setTableData(data.files);
  }, [value]);
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(ResponseErrorPanel, { error });
  }
  if (!value) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "warning" }, "No code coverage found for ", humanizeEntityRef(entity));
  }
  const moveDownIntoPath = (path) => {
    const nextPathData = tableData.find(
      (d) => d.path === path
    );
    if (nextPathData && nextPathData.files) {
      setTableData(nextPathData.files);
    }
  };
  const moveUpIntoPath = (idx) => {
    const path = curPath.split("/").slice(0, idx + 1);
    setCurFile("");
    setCurPath(path.join("/"));
    setTableData(getObjectsAtPath(curData, path.slice(1)));
  };
  const columns = [
    {
      title: "Path",
      type: "string",
      field: "path",
      render: (row) => /* @__PURE__ */ React.createElement(
        Box,
        {
          display: "flex",
          alignItems: "center",
          role: "button",
          tabIndex: row.tableData.id,
          className: styles.link,
          onClick: () => {
            if (row.files?.length) {
              setCurPath(`${curPath}/${row.path}`);
              moveDownIntoPath(row.path);
            } else {
              setCurFile(`${curPath.slice(1)}/${row.path}`);
              setModalOpen(true);
            }
          }
        },
        row.files?.length > 0 && /* @__PURE__ */ React.createElement(FolderIcon, { fontSize: "small", className: styles.icon }),
        row.files?.length === 0 && /* @__PURE__ */ React.createElement(FileOutlinedIcon, { fontSize: "small", className: styles.icon }),
        row.path
      )
    },
    {
      title: "Coverage",
      type: "numeric",
      field: "coverage",
      render: (row) => `${row.coverage.toFixed(2)}%`
    },
    {
      title: "Missing lines",
      type: "numeric",
      field: "missing"
    },
    {
      title: "Tracked lines",
      type: "numeric",
      field: "tracked"
    }
  ];
  const pathArray = curPath.split("/");
  const lastPathElementIndex = pathArray.length - 1;
  const fileCoverage = value.files.find(
    (f) => f.filename.endsWith(curFile)
  );
  if (!fileCoverage) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Box, { className: styles.container }, /* @__PURE__ */ React.createElement(
    Table,
    {
      emptyContent: /* @__PURE__ */ React.createElement(React.Fragment, null, "No files found"),
      data: tableData || [],
      columns,
      title: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, null, "Explore Files"), /* @__PURE__ */ React.createElement(
        Box,
        {
          mt: 1,
          style: {
            fontSize: "0.875rem",
            fontWeight: "normal",
            display: "flex"
          }
        },
        pathArray.map((pathElement, idx) => /* @__PURE__ */ React.createElement(Fragment, { key: pathElement || "root" }, /* @__PURE__ */ React.createElement(
          "div",
          {
            role: "button",
            tabIndex: idx,
            className: idx !== lastPathElementIndex ? styles.link : void 0,
            onKeyDown: () => moveUpIntoPath(idx),
            onClick: () => moveUpIntoPath(idx)
          },
          pathElement || "root"
        ), idx !== lastPathElementIndex && /* @__PURE__ */ React.createElement("div", null, "\xA0/\xA0")))
      ))
    }
  ), /* @__PURE__ */ React.createElement(
    Modal,
    {
      open: modalOpen,
      onClick: (event) => event.stopPropagation(),
      onClose: () => setModalOpen(false),
      style: { overflow: "scroll" }
    },
    /* @__PURE__ */ React.createElement(FileContent, { filename: curFile, coverage: fileCoverage })
  ));
};

export { FileExplorer, buildFileStructure, getObjectsAtPath, groupByPath };
//# sourceMappingURL=FileExplorer.esm.js.map
